

import { usePathname } from 'next/navigation';
import { useTheme } from 'next-themes';
import { Rocket, LayoutDashboard, Zap, BadgeDollarSign, Network, Layers, Settings2, Image, User, ChartColumn, LucideIcon } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/src/lib/shadcn/cn';
import { ThemeSwitch } from '../../theme-switch';
import { useState } from 'react';
import { removeAuthToken } from '@/src/lib/axios/auth-token';

type MenuItem = {
  name: string;
  icon: LucideIcon;
  path: string;
};

export const menuItems: MenuItem[] = [
  {
    name: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
  },
  {
    name: 'Traffic',
    icon: Zap,
    path: '/traffics',
  },
  {
    name: 'Offers',
    icon: BadgeDollarSign,
    path: '/offers',
  },
  {
    name: 'Networks',
    icon: Network,
    path: '/networks',
  },
  {
    name: 'Landers',
    icon: Layers,
    path: '/landing-pages',
  },
  {
    name: 'Ads Builder',
    icon: Image,
    path: '/dashboard/ads-builder',
  },
  {
    name: 'Reports',
    icon: ChartColumn,
    path: '/reports',
  },
  {
    name: 'More',
    icon: Settings2,
    path: '/more',
  },
];

export function Sidebar() {
  const pathname = usePathname();
  const { theme } = useTheme();
  const [showMenu, setShowMenu] = useState(false);

  const handleLogout = () => {
    removeAuthToken();

    if (typeof window !== 'undefined') {
      window.location.href = '/auth/signin';
    }
  };

  return (
    <>
      <svg className="fixed z-[-1]">
        <defs>
          <linearGradient id="purple-gray" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop stopColor="#A78BFA" offset="0%" />
            <stop stopColor="#4B5563" offset="100%" />
          </linearGradient>
          <linearGradient id="blue-purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop stopColor="#6EA8FE" offset="0%" />
            <stop stopColor="#A78BFA" offset="100%" />
          </linearGradient>
          <linearGradient id="pink-gray-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop stopColor="#FF69B4" offset="0%" />
            <stop stopColor="#FF1493" offset="40%" />
            <stop stopColor="#E0E0E0" offset="100%" />
          </linearGradient>
        </defs>
      </svg>

      <aside
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen w-[84px] flex-col justify-between rounded-r-2xl bg-white dark:bg-gray-900 p-3 shadow-lg',
        )}>
        <div className="flex flex-col items-center space-y-6">
          {/* Logo section */}
          <div className="flex items-center justify-center py-3">
            <Link href="/">
              <Rocket className="w-10 h-10" style={{ stroke: 'url(#purple-gray)' }} />
            </Link>
          </div>

          {/* Main navigation */}
          <nav className="flex flex-col items-center space-y-4 text-sm">
            {menuItems.map((item) => {
              // Check if current pathname matches or starts with the item path
              const isActive = item.path === pathname || pathname.startsWith(item.path + '/');

              return (
                <Link
                  key={item.path}
                  href={item.path}
                  className={cn(
                    'flex flex-col items-center justify-center p-3 transition-all duration-200',
                    'hover:text-primary',
                    isActive ? '' : 'text-gray-500 dark:text-gray-400',
                  )}>
                  <item.icon
                    className="h-6 w-6"
                    style={{
                      stroke: isActive
                        ? `url(#${theme === 'dark' ? 'blue-purple-gradient' : 'pink-gray-gradient'})`
                        : 'currentColor',
                    }}
                  />
                  <span className="mt-1 text-xs text-center">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Footer section */}
        <div className="flex flex-col items-center space-y-4 pb-4" data-testid="theme-switch">
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="flex items-center justify-center p-3">
              <User className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            </button>
            {showMenu && (
              <div className="absolute bottom-full mb-2 w-32 bg-white dark:bg-gray-800 rounded-md shadow-lg">
                <button
                  onClick={handleLogout}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Log out
                </button>
              </div>
            )}
          </div>
          <ThemeSwitch />
        </div>
      </aside>
    </>
  );
}
